import React, { useState, Fragment, useEffect } from 'react';

import { getTutorials } from '../../../../store/tutorials';
import { useAppSelector } from '../../../../store/configureStore';
import { tutorialConstants } from '../../../../utils/constants';
import { ETCoverData, ETIReportItem, ETIReportOverview, ETWallThicknessItem, VTEquipmentAnomalyData } from '../../../../utils/models';

import Button from '../../../themed/button';
import Elevation from '../../../themed/instruction-modals/elevation';
import ETCoverForm from '../../../forms/et/etCoverForm';
import ETDrawingForm from '../../../forms/et/etDrawingForm';
import ETInspectionOverviewForm from '../../../forms/et/etInspectionOverviewForm';
import ETWallThicknessDataForm from '../../../forms/et/etWallThicknessDataForm';
import ETReport from './';
import ETSummaryForm from '../../../forms/et/etSummaryForm';
import ETAnomalySummary from './etAnomalySummary';
import Modal from '../../../themed/modal';
import Zippy from '../../../themed/zippy';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

const CreateETReport = () => {
	const [etCoverData, setETCoverData] = useState<ETCoverData | null>();
	const [summarySheetData, setSummarySheetData] = useState();
	const [drawingImages, setDrawingImages] = useState([]);
	const [etWallThicknessData, setETWallThicknessData] = useState<ETWallThicknessItem[]>([]);
	const [eciData, setECIData] = useState<ETIReportItem[]>([]);
	const [eciOverview, setECIOverview] = useState<ETIReportOverview | null>();
	const [anomalyData, setAnomalyData] = useState<VTEquipmentAnomalyData[]>([]);
	const [previewReport, setPreviewReport] = useState<boolean>(false);

	const getReportDetails = () => {
		return {
			coverSheetData: etCoverData,
			summarySheetData,
			eciOverview,
			etWallThicknessData,
			currentTestingData: eciData,
			anomalyData,
			drawingImages,
			projectDetails,
		};
	};

	const handleSaveAndPreview = () => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setAnomalyData(cachedReport.anomalyData);
			setETCoverData(cachedReport.coverSheetData);
			setETWallThicknessData(cachedReport.etWallThicknessData);
			setECIOverview(cachedReport.eciOverview);
			setECIData(cachedReport.currentTestingData);
			setDrawingImages(cachedReport.drawingImages);
			setSummarySheetData(cachedReport.summarySheetData);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<ETReport {...reportDetails} />
			</Modal>

			<Zippy headerTitle="Cover Sheet" isValid={!!etCoverData}>
				<ETCoverForm onProceed={setETCoverData} />
			</Zippy>

			<Zippy headerTitle="Summary Sheet" isValid={!!summarySheetData}>
				<ETSummaryForm onProceed={setSummarySheetData} />
			</Zippy>

			<Zippy headerTitle="Drawing Page" isValid={drawingImages.length > 0}>
				<ETDrawingForm onProceed={setDrawingImages} />
			</Zippy>

			<Zippy
				headerTitle="Diamensional & Wall Thickness Page"
				isValid={etWallThicknessData.length > 0}
			>
				<ETWallThicknessDataForm onProceed={setETWallThicknessData} />
			</Zippy>

			<Zippy
				headerTitle="Eddy current Inspection Report Overview (ET)"
				isValid={!!eciOverview}
			>
				<ETInspectionOverviewForm onProceed={setECIOverview} />
			</Zippy>


			<Zippy headerTitle="Anomaly Data" isValid={anomalyData.length > 0}>
				<ETAnomalySummary
					anomalyData={anomalyData}
					onDataChange={setAnomalyData}
				/>
			</Zippy>

			<div className="d-flex justify-content-end">
				<Elevation
					isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}
				>
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CreateETReport;
